import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, X, MessageCircle, Phone, Zap, Camera, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import { useTenant } from '@/contexts/TenantContext';
import { getApiUrl } from '@/lib/api';

// One-tap canned messages so drivers can keep customers updated without
// typing while behind the wheel. Order = priority — most common first.
const DRIVER_QUICK_REPLIES_DEFAULT = [
  "On my way",
  "I'm outside",
  "Running 5 min late",
  "Running 10 min late",
  "Can't find you — please share your exact location",
  "Arrived — I'll wait right here",
  "Thanks!",
];

// Two-tap customer replies — the customer side of a typical chat. Kept short
// so the row fits without scrolling on the smallest phones.
const CUSTOMER_QUICK_REPLIES_DEFAULT = [
  "Thank you!",
  "I'm on my way down",
  "Please leave at door",
  "Running a couple min late",
  "Please call me",
];

export default function ChatWindow({ bookingId, otherPartyName, otherPartyPhone, onClose }) {
  const { user, token } = useAuth();
  const { messages, sendMessage, fetchMessages } = useRealtime();
  const { tenant } = useTenant();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Effective quick-replies: tenant override wins, otherwise defaults —
  // separate lists for drivers vs customers.
  const isDriver = user?.role === 'driver';
  const tenantTemplates = tenant?.chat_templates;
  const driverQuickReplies = (tenantTemplates && tenantTemplates.length > 0)
    ? tenantTemplates
    : DRIVER_QUICK_REPLIES_DEFAULT;
  const customerQuickReplies = (tenant?.customer_chat_templates && tenant.customer_chat_templates.length > 0)
    ? tenant.customer_chat_templates
    : CUSTOMER_QUICK_REPLIES_DEFAULT;
  const quickReplies = isDriver ? driverQuickReplies : customerQuickReplies;

  useEffect(() => {
    if (bookingId) {
      fetchMessages(bookingId);
    }
  }, [bookingId, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;

    setSending(true);
    await sendMessage(bookingId, text);
    setText('');
    setSending(false);
  };

  const sendQuick = async (canned) => {
    if (sending) return;
    setSending(true);
    try {
      await sendMessage(bookingId, canned);
    } finally {
      setSending(false);
    }
  };

  // Driver-only photo attachment. Uploads to /api/messages/upload-photo,
  // then fires a chat message with the returned URL so the customer sees
  // the photo inline. Keeps text empty by default — the photo IS the message.
  const handlePhotoPick = async (evt) => {
    const file = evt.target.files?.[0];
    evt.target.value = "";
    if (!file || uploading || sending) return;
    if (!file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("booking_id", bookingId);
      fd.append("file", file);
      const res = await fetch(`${getApiUrl()}/api/messages/upload-photo`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(`upload failed (${res.status})`);
      const body = await res.json();
      if (!body.photo_url) throw new Error("no photo_url");
      const caption = (text || "").trim() || "📷 Photo proof";
      await sendMessage(bookingId, caption, body.photo_url);
      setText("");
    } catch (err) {
      console.error("chat photo upload failed", err);
    } finally {
      setUploading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#050505] flex flex-col" data-testid="chat-window">
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#0047FF]/20 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-[#4D88FF]" />
          </div>
          <div>
            <h2 className="font-semibold text-white">{otherPartyName || 'Chat'}</h2>
            <p className="text-xs text-zinc-500">Tap to call</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {otherPartyPhone && (
            <Button
              variant="ghost"
              size="icon"
              className="text-emerald-400 hover:bg-emerald-500/10"
              onClick={() => window.open(`tel:${otherPartyPhone}`, '_self')}
            >
              <Phone className="w-5 h-5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="text-zinc-400 hover:text-white"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-10">
            <MessageCircle className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No messages yet</p>
            <p className="text-xs text-zinc-600 mt-1">Send a message to start the conversation</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            // Driver: my messages match my user id. Customer (no auth): sender_role 'customer' is me.
            const isMe = user?.id ? msg.sender_id === user.id : msg.sender_role === 'customer';
            return (
              <div
                key={msg.id || index}
                className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    isMe
                      ? 'bg-[#0047FF] text-black rounded-br-sm'
                      : 'bg-zinc-800 text-white rounded-bl-sm'
                  }`}
                >
                  {msg.photo_url && (
                    <a
                      href={msg.photo_url}
                      target="_blank"
                      rel="noreferrer"
                      className="block mb-1"
                      data-testid={`chat-photo-link-${msg.id}`}
                    >
                      <img
                        src={msg.photo_url}
                        alt="Photo attachment"
                        className="rounded-lg max-h-64 w-auto object-cover border border-black/20"
                        data-testid={`chat-photo-${msg.id}`}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </a>
                  )}
                  {msg.text && <p className="text-sm">{msg.text}</p>}
                  <p className={`text-xs mt-1 ${isMe ? 'text-white/80' : 'text-zinc-500'}`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-[#121212] border-t border-white/5 p-3">
        {(isDriver || quickReplies.length > 0) && (
          <div
            className="flex items-center gap-2 pb-2 overflow-x-auto scrollbar-none"
            style={{ scrollbarWidth: "none" }}
            data-testid="chat-quick-replies"
          >
            <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500 pr-1">
              <Zap className="w-3 h-3" /> Quick
            </span>
            {quickReplies.map((canned, i) => (
              <button
                key={i}
                type="button"
                onClick={() => sendQuick(canned)}
                disabled={sending}
                className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs bg-[#0047FF]/10 text-[#7AADFF] border border-[#0047FF]/30 hover:bg-[#0047FF]/20 hover:text-white transition-colors disabled:opacity-40"
                data-testid={`chat-quick-reply-${i}`}
                title={canned}
              >
                {canned}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          {isDriver && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoPick}
                className="hidden"
                data-testid="chat-photo-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || sending}
                variant="outline"
                className="border-white/10 text-zinc-300 hover:text-white rounded-xl px-3"
                data-testid="chat-photo-btn"
                title="Attach a photo (delivery proof, curbside arrival, etc.)"
              >
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
              </Button>
            </>
          )}
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 bg-zinc-800 border-white/10 text-white"
            data-testid="chat-input"
          />
          <Button
            onClick={handleSend}
            disabled={!text.trim() || sending}
            className="bg-[#0047FF] hover:bg-[#003BE0] text-white rounded-xl border-0 px-4"
            data-testid="send-button"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
